<template>
  <span
    v-if="verified !== null"
    class="verification-badge"
    :class="verified ? 'is-verified' : 'is-unverified'"
    :title="tooltip"
    role="img"
    :aria-label="ariaLabel"
  >
    <VIcon :name="verified ? 'check-solid' : 'close-solid'" class="verification-badge__icon" />
  </span>
</template>

<script>
/**
 * VerificationBadge
 *
 * Small status circle shown on top of a channel's icon to indicate whether that
 * channel's connection credentials verified successfully.
 *
 *   verified === true   → green circle with white tick
 *   verified === false  → red circle with white cross
 *   verified === null   → nothing rendered (status unknown / not loaded)
 *
 * Backed by the GET social/v1/connections/verify API via
 * useConnectionVerification(). Uses the Vivid VIcon for the glyph and theme
 * colour variables so it stays consistent with the Vivid migration.
 */
export default {
  name: 'VerificationBadge',
  props: {
    /** true = verified, false = failed, null = unknown (hidden). */
    verified: {
      type: Boolean,
      default: null,
    },
    /** Optional error/status text shown on hover for the failed state. */
    errorMessage: {
      type: String,
      default: '',
    },
  },
  computed: {
    tooltip() {
      if (this.verified) return 'Connection verified'
      return this.errorMessage || 'Connection not verified'
    },
    ariaLabel() {
      return this.verified ? 'Connection verified' : 'Connection not verified'
    },
  },
}
</script>

<style scoped>
.verification-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  color: var(--white);
  flex-shrink: 0;
  box-shadow: 0 0 0 2px var(--white);
}
.verification-badge.is-verified {
  background-color: var(--green);
}
.verification-badge.is-unverified {
  background-color: var(--orange);
}
.verification-badge__icon {
  font-size: 11px;
  line-height: 1;
  color: var(--white);
}
</style>
